import { supabase } from "./supabase";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DossierMembre {
  id: string;
  dossier_id: string;
  user_id: string;
  role: "owner" | "daf" | "directeur_financier" | "comptable";
  acces_gestion: boolean;
  acces_compta: boolean;
  created_at: string;
  user: {
    id: string;
    email: string;
    raw_user_meta_data?: { nom?: string; prenom?: string };
  };
}

export interface DossierInvitation {
  id: string;
  dossier_id: string;
  email: string;
  role: "daf" | "directeur_financier" | "comptable";
  acces_gestion: boolean;
  acces_compta: boolean;
  status: "pending" | "accepted" | "expired" | "cancelled";
  token: string;
  created_at: string;
  expires_at: string;
}

// ─── Fetch ───────────────────────────────────────────────────────────────────

type RpcCommentaireAuthorRow = {
  user_id: string;
  email: string | null;
  prenom: string | null;
  nom: string | null;
};

type MembreDisplayInfo = { email: string; prenom: string; nom: string };

export async function fetchDossierMembres(dossierId: string): Promise<DossierMembre[]> {
  const { data: membres, error } = await supabase
    .from("dossier_membres")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  if (!membres?.length) return [];

  const userIds = [...new Set(membres.map((m) => m.user_id))];
  const { data: authorRows, error: rpcError } = await supabase.rpc("get_commentaire_author_info", {
    p_user_ids: userIds,
  });

  if (rpcError) {
    console.warn("[equipe] get_commentaire_author_info", rpcError);
  }

  const rows = (authorRows ?? []) as RpcCommentaireAuthorRow[];
  const byUserId = new Map<string, MembreDisplayInfo>(
    rows.map((row) => [
      row.user_id,
      {
        email: row.email ?? "",
        prenom: (row.prenom ?? "").trim(),
        nom: (row.nom ?? "").trim(),
      },
    ])
  );

  return membres.map((m) => {
    const info = byUserId.get(m.user_id);
    return {
      ...m,
      user: {
        id: m.user_id,
        email: info?.email || "—",
        raw_user_meta_data:
          info?.prenom || info?.nom
            ? { prenom: info.prenom || undefined, nom: info.nom || undefined }
            : undefined,
      },
    };
  }) as DossierMembre[];
}

export async function fetchDossierInvitations(dossierId: string): Promise<DossierInvitation[]> {
  const { data, error } = await supabase
    .from("dossier_invitations")
    .select("*")
    .eq("dossier_id", dossierId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []) as DossierInvitation[];
}

// ─── Actions ─────────────────────────────────────────────────────────────────

type InviteRoleInput = "daf" | "directeur_financier" | "comptable";

function normalizeInviteRole(role: InviteRoleInput): "daf" | "directeur_financier" | "comptable" {
  return role === "directeur_financier" ? "directeur_financier" : role;
}

function invitationPayload(
  dossierId: string,
  emailLower: string,
  invitedBy: string,
  role: "daf" | "directeur_financier" | "comptable",
  accesGestion: boolean,
  accesCompta: boolean
) {
  return {
    dossier_id: dossierId,
    email: emailLower,
    role,
    acces_gestion: accesGestion,
    acces_compta: accesCompta,
    invited_by: invitedBy,
  };
}

export async function inviterMembre(
  dossierId: string,
  email: string,
  role: InviteRoleInput,
  accesGestion: boolean,
  accesCompta: boolean
): Promise<DossierInvitation> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Non authentifié");

  const emailLower = email.toLowerCase().trim();
  const roleInput = normalizeInviteRole(role);

  const tryInsert = async (roleToInsert: "daf" | "directeur_financier" | "comptable") => {
    const payload = invitationPayload(dossierId, emailLower, user.id, roleToInsert, accesGestion, accesCompta);
    console.log("[INVITE] payload avant insert dossier_invitations", payload);
    return supabase
      .from("dossier_invitations")
      .insert(payload)
      .select()
      .single();
  };

  let { data: invitation, error } = await tryInsert(roleInput);

  // Compatibilité schéma: certains environnements acceptent 'daf', d'autres 'directeur_financier'.
  if (error && error.code === "23514" && (roleInput === "daf" || roleInput === "directeur_financier")) {
    const fallbackRole = roleInput === "daf" ? "directeur_financier" : "daf";
    console.warn("[INVITE] role refusé par CHECK, tentative fallback", { roleInput, fallbackRole, error });
    const retry = await tryInsert(fallbackRole);
    invitation = retry.data;
    error = retry.error;
  }

  if (error) {
    console.error("[INVITE] erreur insert dossier_invitations", {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
      roleInput,
      dossierId,
      email: emailLower,
      accesGestion,
      accesCompta,
    });
    if (error.code === "23505") throw new Error("Une invitation est déjà en attente pour cet email");
    throw error;
  }

  return invitation as DossierInvitation;
}

export async function annulerInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from("dossier_invitations")
    .update({ status: "cancelled" })
    .eq("id", invitationId);

  if (error) throw error;
}

export async function renvoyerInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from("dossier_invitations")
    .update({
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    })
    .eq("id", invitationId);

  if (error) throw error;
}

export async function supprimerMembre(membreId: string): Promise<void> {
  const { error } = await supabase
    .from("dossier_membres")
    .delete()
    .eq("id", membreId);

  if (error) throw error;
}

export async function modifierAccesMembre(
  membreId: string,
  accesGestion: boolean,
  accesCompta: boolean
): Promise<void> {
  const { error } = await supabase
    .from("dossier_membres")
    .update({ acces_gestion: accesGestion, acces_compta: accesCompta, updated_at: new Date().toISOString() })
    .eq("id", membreId);

  if (error) throw error;
}

export async function modifierRoleMembre(membreId: string, role: InviteRoleInput): Promise<void> {
  const roleInput = normalizeInviteRole(role);
  const ts = new Date().toISOString();
  const payload: {
    role: string;
    acces_gestion?: boolean;
    acces_compta?: boolean;
    updated_at: string;
  } = { role: roleInput, updated_at: ts };
  if (roleInput === "daf" || roleInput === "directeur_financier") {
    payload.acces_gestion = true;
    payload.acces_compta = true;
  }

  let { error } = await supabase.from("dossier_membres").update(payload).eq("id", membreId);

  if (error && error.code === "23514" && (roleInput === "daf" || roleInput === "directeur_financier")) {
    const fallbackRole = roleInput === "daf" ? "directeur_financier" : "daf";
    const retryPayload = { ...payload, role: fallbackRole };
    const retry = await supabase.from("dossier_membres").update(retryPayload).eq("id", membreId);
    error = retry.error;
  }

  if (error) throw error;
}

export async function accepterInvitation(token: string): Promise<string> {
  const { data, error } = await supabase.rpc("accept_dossier_invitation", { p_token: token });
  if (error) throw error;
  return data as string;
}

export function getInvitationLink(token: string): string {
  return `${window.location.origin}/equipe/invitation?token=${token}`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function getRoleLabel(role: DossierMembre["role"]): string {
  switch (role) {
    case "owner":
      return "Propriétaire";
    case "daf":
    case "directeur_financier":
      return "Directeur financier";
    case "comptable":
      return "Comptable";
    default:
      return role;
  }
}

export function getRoleColor(role: DossierMembre["role"]): { bg: string; color: string } {
  switch (role) {
    case "owner":
      return { bg: "#FEF3C7", color: "#B45309" };
    case "daf":
    case "directeur_financier":
      return { bg: "#EEF2FF", color: "#4338CA" };
    case "comptable":
      return { bg: "#E0F2FE", color: "#0369A1" };
    default:
      return { bg: "#F3F4F6", color: "#6B7280" };
  }
}

export function getInitiales(membre: DossierMembre): string {
  const meta = membre.user.raw_user_meta_data;
  const p0 = meta?.prenom?.[0];
  const n0 = meta?.nom?.[0];
  if (p0 && n0) return `${p0}${n0}`.toUpperCase();
  if (p0) return `${p0}${meta?.prenom?.[1] ?? membre.user.email[0] ?? ""}`.toUpperCase().slice(0, 2);
  if (n0) return `${n0}${meta?.nom?.[1] ?? membre.user.email[0] ?? ""}`.toUpperCase().slice(0, 2);
  return (membre.user.email || "??").slice(0, 2).toUpperCase();
}

export function getNomComplet(membre: DossierMembre): string {
  const meta = membre.user.raw_user_meta_data;
  const prenom = meta?.prenom?.trim();
  const nom = meta?.nom?.trim();
  if (prenom && nom) return `${prenom} ${nom}`;
  if (prenom) return prenom;
  if (nom) return nom;
  return membre.user.email;
}
